<script setup lang="ts">
import { computed } from 'vue';
import { useField } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import ColorPicker from '@/components/ColorPicker.vue';
import RoleVisibilityGuard from '@/components/RoleVisibilityGuard.vue';
import StandardInput from '@/components/StandardInput.vue';
import StandardToggle from '@/components/StandardToggle.vue';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';
import { getTranslation } from '@/composables/data-display-utils';
import type { CommunityBrandingFontSource, CommunityBrandingMode, Locales } from '@/services/types/api.types';
import { useConfigurationStore } from '@/stores';

const { t } = useI18n();
const configurationStore = useConfigurationStore();

const { value: brandingModeField } = useField<CommunityBrandingMode | null>('options.branding.mode');
const { value: languageEnabledField } = useField<Locales[]>('language_enabled');
const { value: headingFontSourceField } = useField<CommunityBrandingFontSource | null>(
  'options.branding.fonts.heading.source',
);
const { value: bodyFontSourceField } = useField<CommunityBrandingFontSource | null>(
  'options.branding.fonts.body.source',
);

const brandingPresetOptions = computed(() =>
  configurationStore.getBrandingPresets().map((preset) => ({
    label: getTranslation({ label: preset.label }, 'label', languageEnabledField.value) || preset.key,
    value: preset.key,
  })),
);

const brandingFontSourceOptions = computed(() => [
  { label: t('community.branding.sources.builtin'), value: 'builtin' },
  { label: t('community.branding.sources.remote-css'), value: 'remote-css' },
]);

const brandingBuiltinFontOptions = computed(() => [
  { label: t('community.branding.fonts.builtin_options.portdequebec'), value: 'portdequebec' },
]);

const brandingColorFields = [
  { key: 'bg', labelKey: 'community.branding.colors.bg' },
  { key: 'surface', labelKey: 'community.branding.colors.surface' },
  { key: 'surface_raised', labelKey: 'community.branding.colors.surface_raised' },
  { key: 'text', labelKey: 'community.branding.colors.text' },
  { key: 'muted', labelKey: 'community.branding.colors.muted' },
  { key: 'brand', labelKey: 'community.branding.colors.brand' },
  { key: 'brand_contrast', labelKey: 'community.branding.colors.brand_contrast' },
  { key: 'link', labelKey: 'community.branding.colors.link' },
  { key: 'border', labelKey: 'community.branding.colors.border' },
  { key: 'focus_ring', labelKey: 'community.branding.colors.focus_ring' },
] as const;

const isCustomBranding = computed(() => brandingModeField.value === 'custom');
const isPresetBranding = computed(() => !isCustomBranding.value);
</script>

<template>
  <role-visibility-guard :requires-superadmin="true">
    <q-card class="q-mb-md">
      <q-card-section>
        <q-item-label header class="text-h6">{{ t('community.branding.title') }}</q-item-label>
      </q-card-section>
      <q-separator />
      <q-card-section>
        <standard-toggle
          name="options.branding.mode"
          :label="t('community.branding.use_preset')"
          :true-value="'default'"
          :false-value="'custom'"
        />
        <localized-select
          v-if="isPresetBranding"
          name="options.branding.preset_key"
          :label="t('community.branding.preset')"
          :options="brandingPresetOptions"
          emit-value
          map-options
          outlined
          class="q-mt-md"
        />
        <div class="row q-col-gutter-md q-mt-sm">
          <div class="col-12 col-md-6">
            <q-item-label header class="text-subtitle1">{{ t('community.branding.fonts.heading.title') }}</q-item-label>
            <localized-select
              name="options.branding.fonts.heading.source"
              :label="t('community.branding.fonts.source')"
              :options="brandingFontSourceOptions"
              emit-value
              map-options
              outlined
              :disabled="!isCustomBranding"
            />
            <standard-input
              name="options.branding.fonts.heading.family"
              :label="t('community.branding.fonts.family')"
              outlined
              :disabled="!isCustomBranding"
            />
            <localized-select
              name="options.branding.fonts.heading.builtin_key"
              :label="t('community.branding.fonts.builtin')"
              :options="brandingBuiltinFontOptions"
              emit-value
              map-options
              outlined
              :disabled="!isCustomBranding || headingFontSourceField !== 'builtin'"
            />
            <standard-input
              name="options.branding.fonts.heading.css_url"
              :label="t('community.branding.fonts.remote_css_url')"
              outlined
              :disabled="!isCustomBranding || headingFontSourceField !== 'remote-css'"
            />
            <standard-input
              name="options.branding.fonts.heading.fallback"
              :label="t('community.branding.fonts.fallback')"
              outlined
              :disabled="!isCustomBranding"
            />
          </div>
          <div class="col-12 col-md-6">
            <q-item-label header class="text-subtitle1">{{ t('community.branding.fonts.body.title') }}</q-item-label>
            <localized-select
              name="options.branding.fonts.body.source"
              :label="t('community.branding.fonts.source')"
              :options="brandingFontSourceOptions"
              emit-value
              map-options
              outlined
              :disabled="!isCustomBranding"
            />
            <standard-input
              name="options.branding.fonts.body.family"
              :label="t('community.branding.fonts.family')"
              outlined
              :disabled="!isCustomBranding"
            />
            <localized-select
              name="options.branding.fonts.body.builtin_key"
              :label="t('community.branding.fonts.builtin')"
              :options="brandingBuiltinFontOptions"
              emit-value
              map-options
              outlined
              :disabled="!isCustomBranding || bodyFontSourceField !== 'builtin'"
            />
            <standard-input
              name="options.branding.fonts.body.css_url"
              :label="t('community.branding.fonts.remote_css_url')"
              outlined
              :disabled="!isCustomBranding || bodyFontSourceField !== 'remote-css'"
            />
            <standard-input
              name="options.branding.fonts.body.fallback"
              :label="t('community.branding.fonts.fallback')"
              outlined
              :disabled="!isCustomBranding"
            />
          </div>
        </div>
        <q-separator :spaced="true" />
        <q-item-label header class="text-subtitle1">{{ t('community.branding.typography.title') }}</q-item-label>
        <div class="row q-col-gutter-md">
          <div class="col-12 col-md-6">
            <standard-input
              name="options.branding.tokens.typography.font_size_body"
              :label="t('community.branding.typography.font_size_body')"
              outlined
              :disabled="!isCustomBranding"
            />
          </div>
          <div class="col-12 col-md-6">
            <standard-input
              name="options.branding.tokens.typography.leading_body"
              :label="t('community.branding.typography.leading_body')"
              outlined
              :disabled="!isCustomBranding"
            />
          </div>
          <div class="col-12 col-md-6">
            <standard-input
              name="options.branding.tokens.typography.font_size_body_mobile"
              :label="t('community.branding.typography.font_size_body_mobile')"
              outlined
              :disabled="!isCustomBranding"
            />
          </div>
          <div class="col-12 col-md-6">
            <standard-input
              name="options.branding.tokens.typography.leading_body_mobile"
              :label="t('community.branding.typography.leading_body_mobile')"
              outlined
              :disabled="!isCustomBranding"
            />
          </div>
        </div>
        <q-separator :spaced="true" />
        <q-item-label header class="text-subtitle1">{{ t('community.branding.colors.title') }}</q-item-label>
        <div class="row q-col-gutter-md">
          <div v-for="field in brandingColorFields" :key="field.key" class="col-12 col-md-6">
            <color-picker
              :color="`options.branding.tokens.colors.${field.key}`"
              :label="t(field.labelKey)"
              :disabled="!isCustomBranding"
              default-color="#ffffff"
            />
          </div>
        </div>
      </q-card-section>
    </q-card>
  </role-visibility-guard>
</template>
